import { useContext, useMemo } from 'react';
import { FsContext } from '../fsData/FsContext';
import { FsVariationToForceContext } from '../fsData/variationsToForceContext';
import { FsCampaign, FsCampaignData, FsVariation, FsVariationToForce, Targetings } from '../type';
import { FsHitsContext } from '../fsData/fsHitsContext';
import { ToastContext } from '../Provider/Toast/ToastContext';

export function useFsContext() {
  const context = useContext(FsContext);
  return context;
}

export function useAllocatedVariations() {
  return useFsContext().fsDataState.allocatedVariations;
}

export function useAllRejectedCampaigns() {
  return useFsContext().fsDataState.allRejectedCampaigns;
}

export function useFsVisitorExposedCampaign() {
  return useFsContext().fsDataState.allExposedCampaigns;
}
export function useFsVariationsToForceContext() {
  return useContext(FsVariationToForceContext);
}

export function useFsVariationsToForce() {
  return useFsVariationsToForceContext().fsVariationToForceState.variationsToForce;
}

export function useTempFsVariationsToForce() {
  return useFsVariationsToForceContext().fsVariationToForceState.variationsToForceTemp;
}

export function useFsVariationsForcedAllocation() {
  return useFsContext().fsDataState.variationsForcedAllocation;
}

export function useFsVariationsForcedUnallocation() {
  return useFsContext().fsDataState.variationsForcedUnallocation;
}

export function useFsEnvId() {
  return useFsContext().fsDataState.fsEnvId;
}

export function useBucketingFile() {
  return useFsContext().fsDataState.bucketingFile;
}

export function useIsFs() {
  return !!useFsContext()?.fsDataState?.fsEnvId;
}

/**
 * Gets both forced and allocated variations for a specific campaign
 * @param campaignId - The campaign ID to get variations for
 * @returns Object containing forcedVariation (if manually set) and visitorVariation (if allocated)
 */
export function useCampaignVariations(campaignId: string) {
  const variationsToForce = useFsVariationsToForce();
  const allocatedVariations = useAllocatedVariations();
  const tempVariationsToForces = useTempFsVariationsToForce();
  const variationsForcedAllocation = useFsVariationsForcedAllocation();
  const variationsForcedUnallocation = useFsVariationsForcedUnallocation();

  const forcedVariation = variationsToForce?.[campaignId] as FsVariationToForce | undefined;
  const allocatedVariation = allocatedVariations?.[campaignId];
  const tempForcedVariation = tempVariationsToForces?.[campaignId] as FsVariationToForce | undefined;
  const variationForcedAllocation = variationsForcedAllocation?.[campaignId] as FsVariationToForce | undefined;
  const variationForcedUnallocation = variationsForcedUnallocation?.[campaignId] as FsVariationToForce | undefined;
  return {
    forcedVariation,
    allocatedVariation,
    tempForcedVariation,
    variationForcedAllocation,
    variationForcedUnallocation,
  };
}

/**
 * Flattens campaign variation groups into a single list of variations and targeting groups
 * @param campaign - The campaign to extract data from
 * @returns Flattened variations array and targeting groups array
 */
export function useFlattenedCampaignData(campaign: FsCampaign): FsCampaignData {
  return useMemo(() => {
    const variations: FsVariation[] = [];
    const targetingGroups: { targetings: Targetings[] }[] = [];

    campaign.variationGroups.forEach((variationGroup) => {
      variations.push(
        ...variationGroup.variations.map((variation) => ({
          ...variation,
          variationGroupId: variationGroup.id,
          variationGroupName: variationGroup.name,
        }))
      );
      targetingGroups.push(...variationGroup.targeting.targetingGroups);
    });

    return { variations, targetingGroups };
  }, [campaign.variationGroups]);
}

export function useFsHitsContext() {
  const context = useContext(FsHitsContext);
  return context;
}

export function useShowToast() {
  const context = useContext(ToastContext);
  return context.showToast;
}

export function useVisitorData() {
  return useFsContext().fsDataState.visitorData;
}
